/**
 * Journey Governance Panel
 *
 * Embedded governance topology visualization for the Journey Navigator.
 * Displays Pistoia Alliance personas with journey-specific assignments.
 *
 * @version 1.0.0
 * @date 2025-11-30
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import { useApiClient } from '../hooks/useApiClient';
import { useJourneyId, useJourneyName } from '../contexts/JourneyContext';
import '../views/settings/governance-topology.css';

// =============================================================================
// TYPES
// =============================================================================

interface Employee {
  employee_id: string;
  display_name: string;
  email: string;
  department?: string;
  job_title?: string;
  is_active: boolean;
}

// DC-1648: the panel's internal persona view-model. Personas now come from
// the per-org FAIR catalog (GET /api/personas, canonical Persona shape) and
// are mapped onto this structure in fetchData — `label` carries the Pistoia
// slug so the slug-keyed related_personas link building below works
// unchanged.
interface GovernancePersonaView {
  persona_id: string;
  label: string;
  persona_name: string;
  definition: string;
  primary_role?: string;
  tasks: string[];
  fair_impact_narrative?: string;
  issues_latent_gains?: string;
  synonyms?: string[];
  related_personas?: string[];
  is_baseline: boolean;
  is_critical: boolean;
  criticality_reason?: string;
  created_at: string;
  updated_at: string;
}

interface Assignment {
  assignment_id: string;
  persona_id: string;
  employee_id: string;
  employee_name: string;
  employee_email: string;
  journey_id: string;
  scope: string;
  assigned_by: string;
  assigned_at: string;
  assignment_notes?: string;
}

interface GraphNode extends d3.SimulationNodeDatum {
  id: string;
  persona: GovernancePersonaView;
  role: string;
  isBaseline: boolean;
  isCritical: boolean;
  assignment?: Assignment;
}

interface GraphLink extends d3.SimulationLinkDatum<GraphNode> {
  source: string | GraphNode;
  target: string | GraphNode;
  type: 'related' | 'role';
}

// Type for links after simulation (source/target become objects with x,y)
interface SimulatedLink {
  source: { x: number; y: number };
  target: { x: number; y: number };
}

interface JourneyGovernancePanelProps {
  journeyId?: string;
  journeyName?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const ROLE_COLORS: Record<string, string> = {
  'Data Producer': '#10b981',
  'Data Enabler': '#3b82f6',
  'Data Consumer': '#8b5cf6',
  'Central': '#f59e0b',
  'unknown': '#6b7280',
};

const ROLE_POSITIONS: Record<string, { x: number; y: number }> = {
  'Data Producer': { x: 0.2, y: 0.3 },
  'Data Enabler': { x: 0.5, y: 0.7 },
  'Data Consumer': { x: 0.8, y: 0.3 },
};

// =============================================================================
// PERSONA DETAIL PANEL
// =============================================================================

interface PersonaDetailPanelProps {
  persona: GovernancePersonaView | null;
  assignment?: Assignment;
  onClose: () => void;
  onAssignClick: (persona: GovernancePersonaView) => void;
}

const PersonaDetailPanel: React.FC<PersonaDetailPanelProps> = ({
  persona,
  assignment,
  onClose,
  onAssignClick,
}) => {
  if (!persona) return null;

  const roleColor = ROLE_COLORS[persona.primary_role || 'unknown'];

  return (
    <div className="persona-detail-panel">
      <div className="panel-header">
        <div className="panel-title-row">
          <h3>{persona.persona_name}</h3>
          <button className="panel-close" onClick={onClose}>&times;</button>
        </div>
        <div className="panel-badges">
          <span className="role-badge" style={{ backgroundColor: roleColor }}>
            {persona.primary_role || 'Unknown Role'}
          </span>
          {persona.is_baseline && <span className="baseline-badge">Baseline</span>}
          {persona.is_critical && <span className="critical-badge">Critical</span>}
        </div>
      </div>

      <div className="panel-body">
        {/* Current Assignment */}
        {assignment && (
          <section className="panel-section assignment-section">
            <h4>Assigned To</h4>
            <div className="assignment-card">
              <div className="assignee-avatar">
                {assignment.employee_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div className="assignee-info">
                <strong>{assignment.employee_name}</strong>
                <span className="assignee-email">{assignment.employee_email}</span>
              </div>
            </div>
          </section>
        )}

        <section className="panel-section">
          <h4>Definition</h4>
          <p>{persona.definition}</p>
        </section>

        {persona.tasks && persona.tasks.length > 0 && (
          <section className="panel-section">
            <h4>Key Tasks</h4>
            <ul className="tasks-list">
              {persona.tasks.slice(0, 5).map((task, idx) => (
                <li key={idx}>{task}</li>
              ))}
              {persona.tasks.length > 5 && (
                <li className="more-tasks">+{persona.tasks.length - 5} more tasks</li>
              )}
            </ul>
          </section>
        )}

        {persona.fair_impact_narrative && (
          <section className="panel-section">
            <h4>FAIR Impact</h4>
            <p className="impact-narrative">{persona.fair_impact_narrative}</p>
          </section>
        )}

        {persona.criticality_reason && (
          <section className="panel-section">
            <h4>Why Critical?</h4>
            <p className="criticality-reason">{persona.criticality_reason}</p>
          </section>
        )}

        {/* Assign Button */}
        <div className="panel-actions">
          <button
            className="btn btn-primary btn-assign"
            onClick={() => onAssignClick(persona)}
          >
            {assignment ? 'Reassign' : 'Assign Team Member'}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// ASSIGNMENT MODAL
// =============================================================================

interface AssignmentModalProps {
  isOpen: boolean;
  persona: GovernancePersonaView | null;
  journeyId: string;
  onClose: () => void;
  onAssign: (personaId: string, employeeId: string, notes: string) => Promise<void>;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

const AssignmentModal: React.FC<AssignmentModalProps> = ({
  isOpen,
  persona,
  journeyId: _journeyId,
  onClose,
  onAssign,
  authFetch,
}) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFetchingEmployees(true);
      authFetch('/api/employees')
        .then((res) => res.json())
        .then((data) => {
          const activeEmployees = (data.employees || []).filter((e: Employee) => e.is_active);
          setEmployees(activeEmployees);
          setFetchingEmployees(false);
        })
        .catch((err) => {
          console.error('[AssignmentModal] Failed to fetch employees:', err);
          setFetchingEmployees(false);
        });

      setSelectedEmployee('');
      setNotes('');
      setError(null);
    }
  }, [isOpen, authFetch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!persona || !selectedEmployee) {
      setError('Please select a team member');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onAssign(persona.persona_id, selectedEmployee, notes);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create assignment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !persona) return null;

  const roleColor = ROLE_COLORS[persona.primary_role || 'unknown'];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content assignment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Assign Team Member</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="assignment-persona-info">
              <div className="persona-preview">
                <span className="persona-dot" style={{ backgroundColor: roleColor }} />
                <div>
                  <strong>{persona.persona_name}</strong>
                  <span className="persona-role-label">{persona.primary_role}</span>
                </div>
              </div>
              <p className="persona-definition-preview">
                {persona.definition?.slice(0, 150)}...
              </p>
            </div>

            {error && <div className="form-error">{error}</div>}

            <div className="form-group">
              <label htmlFor="employee-select">
                Select Team Member <span className="required">*</span>
              </label>
              {fetchingEmployees ? (
                <div className="loading-inline">Loading team members...</div>
              ) : employees.length === 0 ? (
                <div className="empty-employees">
                  <p>No team members available.</p>
                  <a href="/settings/team" className="link-to-team">
                    Add team members first
                  </a>
                </div>
              ) : (
                <select
                  id="employee-select"
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  required
                >
                  <option value="">-- Select a team member --</option>
                  {employees.map((emp) => (
                    <option key={emp.employee_id} value={emp.employee_id}>
                      {emp.display_name} ({emp.email})
                      {emp.job_title ? ` - ${emp.job_title}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="assignment-notes">Notes (Optional)</label>
              <textarea
                id="assignment-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this assignment..."
                rows={3}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !selectedEmployee || employees.length === 0}
            >
              {loading ? 'Assigning...' : 'Assign Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// LEGEND COMPONENT
// =============================================================================

const TopologyLegend: React.FC = () => (
  <div className="topology-legend">
    <h4>Legend</h4>
    <div className="legend-items">
      <div className="legend-section">
        <span className="legend-label">Primary Role:</span>
        {Object.entries(ROLE_COLORS)
          .filter(([k]) => k !== 'Central' && k !== 'unknown')
          .map(([role, color]) => (
            <div key={role} className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: color }} />
              <span>{role}</span>
            </div>
          ))}
      </div>
      <div className="legend-section">
        <span className="legend-label">Status:</span>
        <div className="legend-item">
          <span className="legend-ring baseline" />
          <span>Baseline (Required)</span>
        </div>
        <div className="legend-item">
          <span className="legend-ring assigned" />
          <span>Assigned</span>
        </div>
        <div className="legend-item">
          <span className="legend-ring unassigned" />
          <span>Unassigned</span>
        </div>
      </div>
    </div>
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const JourneyGovernancePanel: React.FC<JourneyGovernancePanelProps> = ({
  journeyId: journeyIdProp,
  journeyName: journeyNameProp,
}) => {
  const journeyId = useJourneyId(journeyIdProp);
  const journeyName = useJourneyName(journeyNameProp);
  const { authFetch } = useApiClient();
  const [personas, setPersonas] = useState<GovernancePersonaView[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPersona, setSelectedPersona] = useState<GovernancePersonaView | null>(null);
  const [filterRole, setFilterRole] = useState<string>('all');
  const [showBaseline, setShowBaseline] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [personaToAssign, setPersonaToAssign] = useState<GovernancePersonaView | null>(null);

  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch personas and assignments
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [personasRes, assignmentsRes] = await Promise.all([
        // DC-1648: per-org FAIR persona catalog (plain Persona[] payload) —
        // the legacy /api/governance/personas endpoint is retired.
        authFetch('/api/personas'),
        authFetch(`/api/governance/assignments?journey_id=${journeyId}`),
      ]);

      if (!personasRes.ok) {
        throw new Error(`Failed to fetch personas: ${personasRes.statusText}`);
      }

      const personasData = (await personasRes.json()) as Array<{
        id: string;
        name: string;
        slug: string | null;
        description: string | null;
        full_description: string | null;
        primary_role: string | null;
        persona_tasks: string | null;
        fair_benefits_description: string | null;
        issues: string | null;
        latent_gains: string | null;
        synonyms: string[] | null;
        related_personas: Array<{ slug: string; relation: string | null }> | null;
        is_baseline: boolean;
        is_critical: boolean;
        criticality_reason: string | null;
        created_at: string;
        updated_at: string;
      }>;
      setPersonas(
        (Array.isArray(personasData) ? personasData : []).map((p) => ({
          persona_id: p.id,
          // label = slug keeps the slug-keyed related_personas link
          // resolution below working unchanged.
          label: p.slug ?? p.name,
          persona_name: p.name,
          definition: p.description ?? p.full_description ?? '',
          primary_role: p.primary_role ?? undefined,
          tasks: p.persona_tasks ? [p.persona_tasks] : [],
          fair_impact_narrative: p.fair_benefits_description ?? undefined,
          issues_latent_gains:
            [p.issues, p.latent_gains].filter(Boolean).join('\n\n') || undefined,
          synonyms: p.synonyms ?? undefined,
          related_personas: p.related_personas?.map((r) => r.slug) ?? undefined,
          is_baseline: p.is_baseline,
          is_critical: p.is_critical,
          criticality_reason: p.criticality_reason ?? undefined,
          created_at: p.created_at,
          updated_at: p.updated_at,
        })),
      );

      if (assignmentsRes.ok) {
        const assignmentsData = await assignmentsRes.json();
        setAssignments(assignmentsData.assignments || []);
      }
    } catch (err: unknown) {
      console.error('[JourneyGovernancePanel] Fetch error:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [journeyId, authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter personas
  const filteredPersonas = useMemo(() => personas.filter((p) => {
    if (showBaseline && !p.is_baseline) return false;
    if (filterRole !== 'all' && p.primary_role !== filterRole) return false;
    return true;
  }), [personas, showBaseline, filterRole]);

  // Map assignments by persona_id
  const assignmentMap = useMemo(() => new Map(assignments.map((a) => [a.persona_id, a])), [assignments]);

  // D3 Graph rendering
  useEffect(() => {
    if (!svgRef.current || !containerRef.current || filteredPersonas.length === 0) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = Math.max(450, container.clientHeight);

    d3.select(svgRef.current).selectAll('*').remove();

    const nodes: GraphNode[] = filteredPersonas.map((p) => ({
      id: p.persona_id,
      persona: p,
      role: p.primary_role || 'unknown',
      isBaseline: p.is_baseline,
      isCritical: p.is_critical,
      assignment: assignmentMap.get(p.persona_id),
    }));

    const links: GraphLink[] = [];
    const nodeIds = new Set(nodes.map((n) => n.id));
    const personaByLabel = new Map(personas.map((p) => [p.label, p.persona_id]));

    filteredPersonas.forEach((p) => {
      if (p.related_personas) {
        p.related_personas.forEach((relLabel) => {
          const relId = personaByLabel.get(relLabel);
          if (relId && nodeIds.has(relId) && p.persona_id !== relId) {
            const exists = links.some(
              (l) =>
                (l.source === p.persona_id && l.target === relId) ||
                (l.source === relId && l.target === p.persona_id)
            );
            if (!exists) {
              links.push({ source: p.persona_id, target: relId, type: 'related' });
            }
          }
        });
      }
    });

    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height].join(' '));

    const g = svg.append('g').attr('class', 'graph-container');

    svg.call(
      d3
        .zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.3, 3])
        .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
          g.attr('transform', event.transform.toString());
        }) as d3.ZoomBehavior<SVGSVGElement, unknown>
    );

    const simulation = d3
      .forceSimulation<GraphNode>(nodes)
      .force(
        'link',
        d3
          .forceLink<GraphNode, GraphLink>(links)
          .id((d) => d.id)
          .distance(100)
          .strength(0.3)
      )
      .force('charge', d3.forceManyBody().strength(-350))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(45))
      .force(
        'x',
        d3.forceX<GraphNode>().x((d) => {
          const pos = ROLE_POSITIONS[d.role];
          return pos ? pos.x * width : width / 2;
        }).strength(0.1)
      )
      .force(
        'y',
        d3.forceY<GraphNode>().y((d) => {
          const pos = ROLE_POSITIONS[d.role];
          return pos ? pos.y * height : height / 2;
        }).strength(0.1)
      );

    const link = g
      .append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('class', 'topology-link')
      .attr('stroke', '#cbd5e1')
      .attr('stroke-width', 1.5)
      .attr('stroke-opacity', 0.6)
      .attr('stroke-dasharray', '4,4');

    const node = g
      .append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('class', (d) =>
        `topology-node ${d.isBaseline ? 'baseline' : ''} ${d.isCritical ? 'critical' : ''} ${d.assignment ? 'assigned' : 'unassigned'}`
      )
      .style('cursor', 'pointer')
      .call(drag(simulation) as unknown as (selection: d3.Selection<d3.BaseType, GraphNode, SVGGElement, unknown>) => void)
      .on('click', (_event, d) => {
        setSelectedPersona(d.persona);
      });

    // Outer ring for baseline/critical/assigned
    node
      .filter((d) => d.isBaseline || d.isCritical || !!d.assignment)
      .append('circle')
      .attr('class', 'node-ring')
      .attr('r', 30)
      .attr('fill', 'none')
      .attr('stroke', (d) => {
        if (d.assignment) return '#10b981'; // Green for assigned
        if (d.isBaseline) return '#f59e0b'; // Amber for baseline
        return '#ef4444'; // Red for critical
      })
      .attr('stroke-width', 3)
      .attr('stroke-dasharray', (d) => (d.isBaseline && !d.assignment ? 'none' : d.assignment ? 'none' : '4,4'));

    // Main circle
    node
      .append('circle')
      .attr('class', 'node-circle')
      .attr('r', 24)
      .attr('fill', (d) => ROLE_COLORS[d.role] || ROLE_COLORS.unknown)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .attr('opacity', (d) => (d.assignment ? 1 : 0.7));

    // Initials
    node
      .append('text')
      .attr('class', 'node-initials')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', '#fff')
      .attr('font-size', '10px')
      .attr('font-weight', '600')
      .attr('pointer-events', 'none')
      .text((d) => {
        const words = d.persona.persona_name.split(/[\s-]+/);
        if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
        return words.slice(0, 2).map((w) => w[0]).join('').toUpperCase();
      });

    // Label
    node
      .append('text')
      .attr('class', 'node-label')
      .attr('text-anchor', 'middle')
      .attr('dy', '44px')
      .attr('fill', '#374151')
      .attr('font-size', '11px')
      .attr('font-weight', '500')
      .attr('pointer-events', 'none')
      .text((d) => truncateLabel(d.persona.persona_name, 14));

    // Checkmark for assigned
    node
      .filter((d) => d.assignment !== undefined)
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '-32px')
      .attr('fill', '#10b981')
      .attr('font-size', '16px')
      .text('✓');

    node.append('title').text((d) => {
      const base = `${d.persona.persona_name}\n${d.role}`;
      if (d.assignment) {
        return `${base}\nAssigned to: ${d.assignment.employee_name}`;
      }
      return `${base}\nNot assigned`;
    });

    simulation.on('tick', () => {
      link
        .attr('x1', (d) => (d as unknown as SimulatedLink).source.x)
        .attr('y1', (d) => (d as unknown as SimulatedLink).source.y)
        .attr('x2', (d) => (d as unknown as SimulatedLink).target.x)
        .attr('y2', (d) => (d as unknown as SimulatedLink).target.y);

      node.attr('transform', (d: GraphNode) => `translate(${d.x ?? 0},${d.y ?? 0})`);
    });

    return () => {
      simulation.stop();
    };
  }, [filteredPersonas, personas, assignments, assignmentMap]);

  function drag(simulation: d3.Simulation<GraphNode, GraphLink>) {
    function dragstarted(event: d3.D3DragEvent<SVGGElement, GraphNode, GraphNode>) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event: d3.D3DragEvent<SVGGElement, GraphNode, GraphNode>) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event: d3.D3DragEvent<SVGGElement, GraphNode, GraphNode>) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    return d3.drag<SVGGElement, GraphNode>()
      .on('start', dragstarted)
      .on('drag', dragged)
      .on('end', dragended);
  }

  function truncateLabel(label: string, maxLen: number): string {
    if (label.length <= maxLen) return label;
    return label.slice(0, maxLen - 1) + '...';
  }

  // Stats
  const stats = {
    total: personas.length,
    assigned: assignments.length,
    unassigned: personas.length - assignments.length,
    baseline: personas.filter((p) => p.is_baseline).length,
    baselineAssigned: personas.filter(
      (p) => p.is_baseline && assignmentMap.has(p.persona_id)
    ).length,
  };

  const roles = ['all', ...Array.from(new Set(personas.map((p) => p.primary_role).filter(Boolean)))];

  const handleAssignClick = (persona: GovernancePersonaView) => {
    setPersonaToAssign(persona);
    setAssignModalOpen(true);
  };

  const handleAssign = async (personaId: string, employeeId: string, notes: string) => {
    const response = await authFetch('/api/governance/assignments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        persona_id: personaId,
        employee_id: employeeId,
        journey_id: journeyId,
        scope: 'journey',
        assignment_notes: notes,
      }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to create assignment');
    }

    // Refresh assignments
    fetchData();
  };

  const selectedAssignment = selectedPersona
    ? assignmentMap.get(selectedPersona.persona_id)
    : undefined;

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <div className="governance-topology journey-governance">
      {/* Header */}
      <div className="topology-header">
        <div className="header-left">
          <h2>Data Governance Roles</h2>
          <p className="subtitle">
            Assign Pistoia Alliance governance personas to team members for {journeyName}
          </p>
        </div>
      </div>

      {/* Quick Access Navigation */}
      <nav className="governance-quick-nav">
        <a href="/settings/team" className="quick-nav-link">
          <span className="nav-icon">👥</span>
          <span className="nav-label">Manage Team</span>
        </a>
        <a href="/ontology" className="quick-nav-link">
          <span className="nav-icon">📚</span>
          <span className="nav-label">Private Ontologies</span>
        </a>
        <a href="/settings/governance" className="quick-nav-link">
          <span className="nav-icon">🏛️</span>
          <span className="nav-label">Full Topology</span>
        </a>
      </nav>

      {/* Progress Bar */}
      <div className="governance-progress">
        <div className="progress-header">
          <span>Assignment Progress</span>
          <span className="progress-value">
            {stats.assigned}/{stats.total} roles assigned
          </span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(stats.assigned / stats.total) * 100}%` }}
          />
        </div>
        <div className="progress-detail">
          <span className="baseline-progress">
            Baseline: {stats.baselineAssigned}/{stats.baseline}
          </span>
          {stats.unassigned > 0 && (
            <span className="unassigned-warning">
              {stats.unassigned} roles need assignment
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="topology-filters">
        <div className="filter-group">
          <label htmlFor="role-filter">Filter by Role:</label>
          <select
            id="role-filter"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
          >
            {roles.map((role) => (
              <option key={role} value={role}>
                {role === 'all' ? 'All Roles' : role}
              </option>
            ))}
          </select>
        </div>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={showBaseline}
            onChange={(e) => setShowBaseline(e.target.checked)}
          />
          Show only baseline personas
        </label>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading governance data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-state">
          <div className="error-icon">Warning</div>
          <h3>Failed to Load Governance Data</h3>
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={fetchData}>
            Retry
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && personas.length === 0 && (
        <div className="empty-state">
          <div className="empty-illustration">Topology</div>
          <h2>No Personas Found</h2>
          <p>
            Pistoia Alliance governance personas haven't been loaded yet.
            Check the Settings &gt; Governance page.
          </p>
        </div>
      )}

      {/* Graph Container */}
      {!loading && !error && filteredPersonas.length > 0 && (
        <div className="topology-main compact">
          <div className="graph-container" ref={containerRef}>
            <svg ref={svgRef} className="topology-svg" />
            <TopologyLegend />
          </div>

          <PersonaDetailPanel
            persona={selectedPersona}
            assignment={selectedAssignment}
            onClose={() => setSelectedPersona(null)}
            onAssignClick={handleAssignClick}
          />
        </div>
      )}

      {/* No Results */}
      {!loading && !error && personas.length > 0 && filteredPersonas.length === 0 && (
        <div className="no-results-state">
          <p>No personas match your current filters.</p>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setFilterRole('all');
              setShowBaseline(false);
            }}
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Assignment Modal */}
      <AssignmentModal
        isOpen={assignModalOpen}
        persona={personaToAssign}
        journeyId={journeyId}
        onClose={() => {
          setAssignModalOpen(false);
          setPersonaToAssign(null);
        }}
        onAssign={handleAssign}
        authFetch={authFetch}
      />
    </div>
  );
};

export default JourneyGovernancePanel;
